<template>
  <div class="choice-container">
    <!-- Header label -->
    <div class="choice-header">
      <h2 class="card-question">{{ title }}</h2>
      <p class="subtitle" v-html="subtitle"></p>
    </div>

    <!-- Card view: untuk final_prodi_choice — pilihan berbentuk kartu prodi -->
    <div v-if="cardView" class="card-choices-wrapper">
      <button
        v-for="choice in choices"
        :key="choice.kode"
        class="choice-card"
        :class="{
          'choice-card--selected': selectedChoices.includes(choice.kode),
          'choice-card--disabled': selectedChoices.length >= maxSelections && !selectedChoices.includes(choice.kode)
        }"
        @click="selectCardChoice(choice)"
        :aria-pressed="selectedChoices.includes(choice.kode)"
      >
        <div class="choice-card__header">
          <span class="choice-card__prodi-name">{{ choice.prodi_nama }}</span>
          <span class="choice-card__check" :class="{ 'choice-card__check--active': selectedChoices.includes(choice.kode) }">
            <svg v-if="selectedChoices.includes(choice.kode)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M173.9 439.4l-166.4-166.4c-10-10-10-26.2 0-36.2l36.2-36.2c10-10 26.2-10 36.2 0L192 312.69l271.1-271.1c10-10 26.2-10 36.2 0l36.2 36.2c10 10 10 26.2 0 36.2l-307.3 307.3c-10 10-26.2 10-36.3 0z"/></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"/></svg>
          </span>
        </div>
        <div class="choice-card__body">
          <p class="choice-card__nama">{{ choice.keterangan }}</p>
          <!-- <span class="choice-card__keterangan-tag">{{ choice.keterangan }}</span> -->
        </div>
      </button>
    </div>

    <!-- Default view: tombol teks biasa (multi_choice) -->
    <div v-else class="choices-wrapper">
      <button
        v-for="(description, code) in choices"
        :key="code"
        class="choice-button"
        :class="{
          selected: isSelected(code),
          'choice-button--disabled': Array.isArray(selectedChoices) && selectedChoices.length >= maxSelections && !isSelected(code)
        }"
        @click="selectChoice(code)"
        :aria-pressed="isSelected(code)"
      >
        <span class="choice-button__check">
          <svg v-if="isSelected(code)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M173.9 439.4l-166.4-166.4c-10-10-10-26.2 0-36.2l36.2-36.2c10-10 26.2-10 36.2 0L192 312.69l271.1-271.1c10-10 26.2-10 36.2 0l36.2 36.2c10 10 10 26.2 0 36.2l-307.3 307.3c-10 10-26.2 10-36.3 0z"/></svg>
        </span>
        <span class="choice-button__text">{{ typeof description === 'object' ? description.text : description }}</span>
      </button>
    </div>

    <p v-if="maxSelections > 1" class="selection-counter">
      {{ Array.isArray(selectedChoices) ? selectedChoices.length : 0 }} / {{ maxSelections }} dipilih
    </p>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useToast } from '../composables/useToast'; // <-- Import "otak" notifikasi

const { addToast } = useToast(); // Siapkan fungsi addToast
const props = defineProps({
  title: { type: String, default: 'Pilih Pernyataan' },
  subtitle: { type: String, default: 'Pilih yang paling sesuai dengan kamu.' },
  choices: { type: [Object, Array], required: true },
  modelValue: { type: [String, Array], required: true },
  maxSelections: { type: Number, default: 1 },
  cardView: { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelValue']);

const selectedChoices = ref(props.modelValue);

// Pastikan watch tidak error jika modelValue berubah tipe
watch(() => props.modelValue, (newValue) => {
  selectedChoices.value = newValue;
});

function isSelected(code) {
  if (Array.isArray(selectedChoices.value)) {
    return selectedChoices.value.includes(code);
  }
  return selectedChoices.value === code;
}

function selectChoice(code) {
  if (props.maxSelections === 1) {
    selectedChoices.value = code;
  } else {
    // Pastikan selectedChoices adalah array sebelum push/splice
    if (!Array.isArray(selectedChoices.value)) {
        selectedChoices.value = [];
    }
    const selectedIndex = selectedChoices.value.indexOf(code);
    if (selectedIndex > -1) {
      selectedChoices.value.splice(selectedIndex, 1);
    } else if (selectedChoices.value.length < props.maxSelections) {
      selectedChoices.value.push(code);
    } else {
      addToast(`Kamu harus memilih tepat ${props.maxSelections} pilihan.`, "error"); // <-- Ganti dengan ini
    }
  }
}

function selectCardChoice(choice) {
  if (!Array.isArray(selectedChoices.value)) selectedChoices.value = [];
  const idx = selectedChoices.value.indexOf(choice.kode);
  if (idx > -1) {
    selectedChoices.value.splice(idx, 1);
  } else if (selectedChoices.value.length < props.maxSelections) {
    selectedChoices.value.push(choice.kode);
  } else {
    addToast(`Maksimal ${props.maxSelections} pilihan.`, 'error');
  }
}

watch(selectedChoices, (newValue) => {
  emit('update:modelValue', newValue);
}, { deep: true });
</script>

<style scoped>
.choice-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ---- Header ---- */
.choice-header {
  background: linear-gradient(135deg, #EFF6FF, #f8faff);
  border: 1.5px solid #dce9f5;
  border-radius: 16px;
  padding: 1.1rem 1.25rem;
  margin-bottom: 1rem;
  text-align: center;
}

.card-question {
  font-size: 1.2rem;
  font-weight: 800;
  color: #26488A;
  margin: 0 0 0.35rem;
  line-height: 1.3;
}

.subtitle {
  color: #5a7a9a;
  font-size: 0.82rem;
  margin: 0;
  line-height: 1.55;
}

/* ---- Default choice list ---- */
.choices-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.choice-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  font-family: 'Epilogue', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: left;
  background: #f8fafc;
  border: 1.5px solid #dce9f5;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s, transform 0.15s;
  line-height: 1.5;
  color: #2c3e50;
  box-shadow: 0 1px 4px rgba(38, 72, 138, 0.05);
}

.choice-button:hover:not(.choice-button--disabled) {
  border-color: #4489C6;
  background: #f0f7ff;
  box-shadow: 0 4px 12px rgba(68, 137, 198, 0.13);
  transform: translateY(-2px);
}

.choice-button.selected {
  border-color: #26488A;
  background: linear-gradient(135deg, #26488A, #3a6abf);
  color: #ffffff;
  font-weight: 600;
  box-shadow: 0 5px 18px rgba(38, 72, 138, 0.28);
}

.choice-button--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.choice-button__check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #c5d8ed;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.2s, background 0.2s;
}

.choice-button.selected .choice-button__check {
  background: #F0D25B;
  border-color: #F0D25B;
}

.choice-button__check svg {
  width: 9px;
  height: 9px;
  fill: #26488A;
}

.choice-button__text { flex: 1; }

/* ---- Selection counter ---- */
.selection-counter {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #4489C6;
}

/* ---- Card view styles ---- */
.card-choices-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.choice-card {
  width: 100%;
  background: #f8f9fa;
  border: 2px solid #e0e8f5;
  border-radius: 14px;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font-family: 'Epilogue', sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  overflow: hidden;
}

.choice-card:hover:not(.choice-card--disabled) {
  border-color: #4489C6;
  box-shadow: 0 4px 14px rgba(68, 137, 198, 0.14);
  transform: translateY(-2px);
}

.choice-card.choice-card--selected {
  border-color: #26488A;
  background: #EFF6FF;
  box-shadow: 0 4px 16px rgba(38, 72, 138, 0.18);
}

.choice-card.choice-card--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.choice-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #26488A;
  padding: 0.65rem 1rem;
  gap: 0.75rem;
}

.choice-card.choice-card--selected .choice-card__header {
  background: #1a3468;
}

.choice-card__prodi-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #ffffff;
  flex: 1;
  line-height: 1.3;
}

.choice-card__check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.5);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s, border-color 0.2s;
}

.choice-card__check--active {
  background: #F0D25B;
  border-color: #F0D25B;
}

.choice-card__check svg {
  width: 10px;
  height: 10px;
  fill: #26488A;
}

.choice-card__body {
  padding: 0.8rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.choice-card__nama {
  margin: 0;
  font-size: 0.875rem;
  color: #34495e;
  line-height: 1.55;
  font-style: italic;
}

/* ---- Mobile ---- */
@media (max-width: 480px) {
  .card-question {
    font-size: 1rem;
  }
  .subtitle {
    font-size: 0.72rem;
  }
  .choice-button {
    font-size: 0.78rem;
  }
  .selection-counter {
    font-size: 0.7rem;
  }
  .choice-card__prodi-name {
    font-size: 0.82rem;
  }
  .choice-card__nama {
    font-size: 0.8rem;
  }
}
</style>